import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import { currentSearch, fetchAdminJson, LoadState } from "./adminApi";

type UniqueIpSets = {
  inbound: Set<string>;
  download: Set<string>;
  purchase: Set<string>;
  activation: Set<string>;
  update_check: Set<string>;
};

type BreakdownRow = {
  label: string;
  count: number;
};

type AircraftRow = {
  name: string;
  author: string;
  filename: string;
  count: number;
};

type AdminDetailProps = {
  date_start?: unknown;
  date_end?: unknown;
  date_starts?: unknown;
  stats?: any;
  inbounds?: any;
  downloads?: any;
  purchases?: any;
  activations?: any;
  update_checks?: any;
  unique_ips?: any;
  activation_counts_by_key?: any;
};

function asArray<T = any>(value: any): T[] {
  return Array.isArray(value) ? value : [];
}

function asNumber(value: any, fallback = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function asString(value: any, fallback = ""): string {
  return value == null ? fallback : String(value);
}

function toDate(value: any): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

function formatDate(value: any): string {
  const date = toDate(value);
  return date == null ? "-" : date.toLocaleDateString();
}

function formatDateTime(value: any): string {
  const date = toDate(value);
  return date == null ? asString(value, "-") : date.toLocaleString();
}

function formatDateInput(value: any): string {
  const date = toDate(value);
  return date == null ? "" : date.toISOString().slice(0, 10);
}

function normalizeToSet(source: any): Set<string> {
  if (source instanceof Set) {
    return new Set(Array.from(source, (item) => asString(item)));
  }
  if (Array.isArray(source)) {
    return new Set(source.map((item) => asString(item)));
  }
  if (source != null && typeof source === "object") {
    const result = new Set<string>();
    Object.entries(source).forEach(([key, value]) => {
      if (Boolean(value)) {
        result.add(key);
      }
    });
    return result;
  }
  return new Set<string>();
}

function buildUniqueIpSets(uniqueIps: any): UniqueIpSets {
  const source = uniqueIps ?? {};
  return {
    inbound: normalizeToSet(source.inbound),
    download: normalizeToSet(source.download),
    purchase: normalizeToSet(source.purchase),
    activation: normalizeToSet(source.activation),
    update_check: normalizeToSet(source.update_check),
  };
}

function activationCountForKey(key: string, activationCountsByKey: any): number {
  if (activationCountsByKey instanceof Map) {
    return asNumber(activationCountsByKey.get(key), 0);
  }
  if (activationCountsByKey != null && typeof activationCountsByKey === "object") {
    return asNumber(activationCountsByKey[key], 0);
  }
  return 0;
}

function makeIDP(ip: any, uniqueIps: UniqueIpSets): string {
  const key = asString(ip);
  let marker = "";
  if (uniqueIps.inbound.has(key)) marker += "I";
  if (uniqueIps.download.has(key)) marker += "D";
  if (uniqueIps.purchase.has(key)) marker += "P";
  if (uniqueIps.activation.has(key)) marker += "A";
  if (uniqueIps.update_check.has(key)) marker += "U";
  return marker;
}

function calcPercentage(a: number, b: number): string {
  return b <= 0 ? "0%" : `${((100 * a) / b).toFixed(1)}%`;
}

function intersectionPercentage(a: Set<string>, b: Set<string>): string {
  if (b.size === 0) {
    return "0%";
  }
  let intersectionCount = 0;
  b.forEach((value) => {
    if (a.has(value)) {
      intersectionCount += 1;
    }
  });
  return calcPercentage(intersectionCount, b.size);
}

function sumNumbers(values: number[]): number {
  return values.reduce((sum, value) => sum + value, 0);
}

function toBreakdownRows(value: any): BreakdownRow[] {
  if (Array.isArray(value)) {
    return value
      .map((item: any): BreakdownRow => {
        if (Array.isArray(item)) {
          return {
            label: asString(item[0], "Unknown"),
            count: asNumber(item[1], 0),
          };
        }
        if (item != null && typeof item === "object") {
          return {
            label: asString(item.version ?? item.name ?? item.label, "Unknown"),
            count: asNumber(item.count ?? item.value, 0),
          };
        }
        return { label: asString(item, "Unknown"), count: 0 };
      })
      .filter((row) => row.label.length > 0);
  }
  if (value != null && typeof value === "object") {
    return Object.entries(value).map(([label, count]) => ({
      label: asString(label, "Unknown"),
      count: asNumber(count, 0),
    }));
  }
  return [];
}

function toAircraftRows(value: any): AircraftRow[] {
  return asArray(value).map((aircraft: any) => ({
    name: asString(aircraft?.name, "Unknown"),
    author: asString(aircraft?.author, "Unknown"),
    filename: asString(aircraft?.filename, "Unknown"),
    count: asNumber(aircraft?.count, 0),
  }));
}

function AdminDetailSearch(props: { dateStart?: unknown; dateEnd?: unknown }) {
  return (
    <div className="bg-dark text-light py-2">
      <Container>
        <form className="d-flex gap-2 flex-wrap">
          <input type="date" name="from" defaultValue={formatDateInput(props.dateStart)} />
          <input type="date" name="to" defaultValue={formatDateInput(props.dateEnd)} />
          <input type="submit" value="Update" />
        </form>
      </Container>
    </div>
  );
}

function AdminDetailStats(props: { dateStart?: unknown; dateEnd?: unknown; dateStarts?: unknown; stats?: any }) {
  const stats = props.stats ?? {};
  const inbounds = asArray(stats.inbounds).map((value) => asNumber(value));
  const downloads = asArray(stats.downloads).map((value) => asNumber(value));
  const purchases = asArray(stats.purchases).map((value) => asNumber(value));
  const activations = asArray(stats.activations).map((value) => asNumber(value));
  const updateChecksUnique = asArray(stats.update_checks_unique).map((value) => asNumber(value));
  const updateChecks = asArray(stats.update_checks).map((value) => asNumber(value));
  const maxLength = Math.max(
    inbounds.length,
    downloads.length,
    purchases.length,
    activations.length,
    updateChecksUnique.length,
    updateChecks.length
  );

  const explicitDates = asArray(props.dateStarts)
    .map((value) => toDate(value))
    .filter((value): value is Date => value != null);

  const columnLabels =
    explicitDates.length === maxLength && maxLength > 0
      ? explicitDates.map((date) => `${date.toLocaleString("en-US", { month: "short" })} ${date.toLocaleString("en-US", { day: "numeric" })}`)
      : Array.from({ length: maxLength }, (_, index) => `Day ${index + 1}`);

  const metricRows = [
    { label: "Inbound", values: inbounds, total: asNumber(stats.total_inbounds, sumNumbers(inbounds)) },
    { label: "Downloads", values: downloads, total: asNumber(stats.total_downloads, sumNumbers(downloads)) },
    { label: "Purchases", values: purchases, total: asNumber(stats.total_purchases, sumNumbers(purchases)) },
    { label: "Activations", values: activations, total: asNumber(stats.total_activations, sumNumbers(activations)) },
    {
      label: "Update chk (unique)",
      values: updateChecksUnique,
      total: asNumber(stats.total_update_checks_unique, sumNumbers(updateChecksUnique)),
    },
    { label: "Update checks", values: updateChecks, total: asNumber(stats.total_update_checks, sumNumbers(updateChecks)) },
  ];

  return (
    <Container className="py-3">
      <h3>
        Detailed stats: {formatDate(props.dateStart)} - {formatDate(props.dateEnd)}
      </h3>
      <Table size="sm">
        <thead className="thead-dark">
          <tr>
            <th></th>
            {columnLabels.map((label) => (
              <th key={label}>{label}</th>
            ))}
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {metricRows.map((row) => (
            <tr key={row.label}>
              <td>{row.label}</td>
              {columnLabels.map((_, index) => (
                <td key={`${row.label}-${index}`}>{asNumber(row.values[index], 0)}</td>
              ))}
              <td>{row.total}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <p>
        {asNumber(stats.pro_update_check_count, 0)} / {asNumber(stats.total_update_checks, 0)} (
        {(asNumber(stats.pro_user_fraction, 0) * 100).toFixed(2)}%) of update checks are pro version
      </p>
    </Container>
  );
}

function BreakdownTable(props: { title: string; rows: BreakdownRow[]; total: number; previewCount?: number }) {
  const [expanded, setExpanded] = useState(false);
  const previewCount = props.previewCount ?? 5;
  const visibleRows = expanded ? props.rows : props.rows.slice(0, previewCount);

  return (
    <>
      <h4>{props.title}</h4>
      <Table size="sm">
        <thead className="thead-dark">
          <tr>
            <th>Count (%)</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row, index) => (
            <tr key={`${props.title}-${row.label}-${index}`}>
              <td>
                {row.count} ({calcPercentage(row.count, props.total)})
              </td>
              <td>{row.label}</td>
            </tr>
          ))}
          {!expanded && props.rows.length > previewCount ? (
            <tr>
              <td colSpan={2}>
                <Button onClick={() => setExpanded(true)}>More...</Button>
              </td>
            </tr>
          ) : null}
        </tbody>
      </Table>
    </>
  );
}

function AircraftTable(props: { rows: AircraftRow[]; total: number }) {
  const [expanded, setExpanded] = useState(false);
  const visibleRows = expanded ? props.rows : props.rows.slice(0, 5);

  return (
    <>
      <h4>Aircraft</h4>
      <Table size="sm">
        <thead className="thead-dark">
          <tr>
            <th>Count (%)</th>
            <th>Name</th>
            <th>Author</th>
            <th>Filename</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row, index) => (
            <tr key={`${row.name}-${row.filename}-${index}`}>
              <td>
                {row.count} ({calcPercentage(row.count, props.total)})
              </td>
              <td>{row.name}</td>
              <td>{row.author}</td>
              <td>{row.filename}</td>
            </tr>
          ))}
          {!expanded && props.rows.length > 5 ? (
            <tr>
              <td colSpan={4}>
                <Button onClick={() => setExpanded(true)}>More...</Button>
              </td>
            </tr>
          ) : null}
        </tbody>
      </Table>
    </>
  );
}

function AdminDetailDemographics(props: { stats?: any }) {
  const stats = props.stats ?? {};
  const total = asNumber(stats.total_update_checks, 0);
  const pluginRows = toBreakdownRows(stats.plugin_version_breakdown);
  const xplaneRows = toBreakdownRows(stats.xplane_version_breakdown);
  const osRows = toBreakdownRows(stats.os_version_breakdown);
  const aircraftRows = toAircraftRows(stats.aircraft);

  return (
    <Container className="py-3">
      <h3>Demographics</h3>
      <Row>
        <Col md={4} className="text-center">
          <BreakdownTable title="Plugin version" rows={pluginRows} total={total} />
        </Col>
        <Col md={4} className="text-center">
          <BreakdownTable title="X-plane version" rows={xplaneRows} total={total} />
        </Col>
        <Col md={4} className="text-center">
          <BreakdownTable title="Operating system" rows={osRows} total={total} />
        </Col>
      </Row>
      <Row>
        <Col md={12} className="text-center">
          <AircraftTable rows={aircraftRows} total={total} />
        </Col>
      </Row>
    </Container>
  );
}

function AdminDetailInbounds(props: { inbounds?: any; uniqueIps: UniqueIpSets }) {
  const inbounds = asArray(props.inbounds);
  const downloadPercentage = intersectionPercentage(props.uniqueIps.download, props.uniqueIps.inbound);
  const purchasePercentage = intersectionPercentage(props.uniqueIps.purchase, props.uniqueIps.inbound);

  return (
    <>
      <h2>Inbounds</h2>
      <p>
        {downloadPercentage} of uniques downloaded · {purchasePercentage} of uniques purchased
      </p>
      <Table size="sm">
        <thead className="thead-dark">
          <tr>
            <th></th>
            <th>#</th>
            <th>Date</th>
            <th>Browser</th>
            <th>Browser version</th>
            <th>Language</th>
            <th>OS</th>
            <th>IP</th>
          </tr>
        </thead>
        <tbody>
          {inbounds.map((inbound: any, index: number) => (
            <tr key={`inbound-${index}`}>
              <td>{makeIDP(inbound?.ip_address, props.uniqueIps)}</td>
              <td>{asString(inbound?.num)}</td>
              <td>{formatDateTime(inbound?.date)}</td>
              <td>{asString(inbound?.browser)}</td>
              <td>{asString(inbound?.browser_version)}</td>
              <td>{asString(inbound?.language)}</td>
              <td>{asString(inbound?.os)}</td>
              <td>{asString(inbound?.ip_address)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}

function AdminDetailDownloads(props: { downloads?: any; uniqueIps: UniqueIpSets }) {
  const downloads = asArray(props.downloads);
  const inboundPercentage = intersectionPercentage(props.uniqueIps.inbound, props.uniqueIps.download);
  const purchasePercentage = intersectionPercentage(props.uniqueIps.purchase, props.uniqueIps.download);

  return (
    <>
      <h2>Downloads</h2>
      <p>
        {inboundPercentage} of uniques from inbounds · {purchasePercentage} of uniques purchased this period
      </p>
      <Table size="sm">
        <thead className="thead-dark">
          <tr>
            <th></th>
            <th>Date</th>
            <th>Browser</th>
            <th>Browser version</th>
            <th>Language</th>
            <th>OS</th>
            <th>IP</th>
          </tr>
        </thead>
        <tbody>
          {downloads.map((download: any, index: number) => (
            <tr key={`download-${index}`}>
              <td>{makeIDP(download?.ip_address, props.uniqueIps)}</td>
              <td>{formatDateTime(download?.date)}</td>
              <td>{asString(download?.browser)}</td>
              <td>{asString(download?.browser_version)}</td>
              <td>{asString(download?.language)}</td>
              <td>{asString(download?.os)}</td>
              <td>{asString(download?.ip_address)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}

function AdminDetailPurchases(props: { purchases?: any; uniqueIps: UniqueIpSets; activationCountsByKey?: any }) {
  const purchases = asArray(props.purchases);
  const inboundPercentage = intersectionPercentage(props.uniqueIps.inbound, props.uniqueIps.purchase);
  const downloadPercentage = intersectionPercentage(props.uniqueIps.download, props.uniqueIps.purchase);

  return (
    <>
      <h2>Purchases</h2>
      <p>
        {inboundPercentage} of uniques from inbounds · {downloadPercentage} of uniques downloaded
      </p>
      <Table size="sm">
        <thead className="thead-dark">
          <tr>
            <th></th>
            <th>License</th>
            <th>Date</th>
            <th>Browser</th>
            <th>IP</th>
          </tr>
        </thead>
        <tbody>
          {purchases.map((purchase: any, index: number) => {
            const licenseKey = asString(purchase?.license_key);
            const activationCount = activationCountForKey(licenseKey, props.activationCountsByKey);
            return (
              <tr key={`purchase-${index}`}>
                <td>{makeIDP(purchase?.ip_address, props.uniqueIps)}</td>
                <td>
                  {asString(purchase?.license_email)}
                  <br />
                  {licenseKey} ({activationCount})
                </td>
                <td>{formatDateTime(purchase?.date ?? purchase?.purchase_date)}</td>
                <td>
                  {asString(purchase?.browser)} {asString(purchase?.browser_version)}
                  <br />
                  {asString(purchase?.language)}
                </td>
                <td>
                  {asString(purchase?.ip_address)}
                  <br />
                  {asString(purchase?.os)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
}

function AdminDetailActivations(props: { activations?: any; uniqueIps: UniqueIpSets; activationCountsByKey?: any }) {
  const activations = asArray(props.activations);
  const downloadPercentage = intersectionPercentage(props.uniqueIps.download, props.uniqueIps.activation);
  const purchasePercentage = intersectionPercentage(props.uniqueIps.purchase, props.uniqueIps.activation);

  return (
    <>
      <h2>Activations</h2>
      <p>
        {downloadPercentage} of uniques downloaded this period · {purchasePercentage} of uniques purchased this period
      </p>
      <Table size="sm">
        <thead className="thead-dark">
          <tr>
            <th></th>
            <th>License</th>
            <th>Result</th>
            <th>Version</th>
            <th>System</th>
          </tr>
        </thead>
        <tbody>
          {activations.map((activation: any, index: number) => {
            const licenseKey = asString(activation?.license_key);
            const activationCount = activationCountForKey(licenseKey, props.activationCountsByKey);
            return (
              <tr key={`activation-${index}`}>
                <td>{makeIDP(activation?.ip_address, props.uniqueIps)}</td>
                <td>
                  {asString(activation?.license_email)}
                  <br />
                  {licenseKey} ({activationCount})
                  <br />
                  {formatDateTime(activation?.license_date)}
                </td>
                <td>
                  {formatDateTime(activation?.date)}
                  <br />
                  {asString(activation?.result)}
                </td>
                <td>
                  {asString(activation?.xplane_version)}
                  <br />
                  {asString(activation?.plugin_version)}
                </td>
                <td>
                  {asString(activation?.os)}
                  <br />
                  {asString(activation?.ip_address)}
                  <br />
                  {asString(activation?.mac_address)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
}

function AdminDetailUpdateChecks(props: { updateChecks?: any; uniqueIps: UniqueIpSets; stats?: any }) {
  const updateChecks = asArray(props.updateChecks);
  const stats = props.stats ?? {};
  const downloadPercentage = intersectionPercentage(props.uniqueIps.download, props.uniqueIps.update_check);
  const purchasePercentage = intersectionPercentage(props.uniqueIps.purchase, props.uniqueIps.update_check);

  return (
    <>
      <h2>Update checks</h2>
      <p>
        {asNumber(stats.total_update_checks_unique, props.uniqueIps.update_check.size)} unique
        <br />
        {downloadPercentage} of uniques downloaded this period · {purchasePercentage} of uniques purchased this period
      </p>
      <Table size="sm">
        <thead className="thead-dark">
          <tr>
            <th></th>
            <th>Date</th>
            <th>License date</th>
            <th>Plugin version</th>
            <th>X-plane version</th>
            <th>OS</th>
            <th>Aircraft</th>
            <th>IP</th>
          </tr>
        </thead>
        <tbody>
          {updateChecks.map((updateCheck: any, index: number) => (
            <tr key={`update-check-${index}`}>
              <td>{makeIDP(updateCheck?.ip_address, props.uniqueIps)}</td>
              <td>{formatDateTime(updateCheck?.date)}</td>
              <td>{formatDateTime(updateCheck?.license_date)}</td>
              <td>{asString(updateCheck?.plugin_version)}</td>
              <td>{asString(updateCheck?.xplane_version)}</td>
              <td>{asString(updateCheck?.os)}</td>
              <td>
                {asString(updateCheck?.aircraft_name ?? updateCheck?.aircraft, "none")}
                {updateCheck?.aircraft_author ? (
                  <>
                    <br />
                    {asString(updateCheck.aircraft_author)}
                  </>
                ) : null}
                {updateCheck?.aircraft_filename ? (
                  <>
                    <br />
                    {asString(updateCheck.aircraft_filename)}
                  </>
                ) : null}
              </td>
              <td>{asString(updateCheck?.ip_address)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}

export function AdminDetail(props: AdminDetailProps) {
  const [state, setState] = useState<LoadState<AdminDetailProps>>(
    props.stats == null ? { status: "loading" } : { status: "loaded", data: props }
  );

  useEffect(() => {
    if (props.stats != null) {
      return;
    }
    fetchAdminJson<AdminDetailProps>(`/detail${currentSearch()}`)
      .then((data) => setState({ status: "loaded", data }))
      .catch((error: Error) => setState({ status: "error", error: error.message }));
  }, []);

  if (state.status === "error") {
    return <div className="alert alert-danger">{state.error}</div>;
  }

  const loadedProps = state.status === "loaded" ? state.data : props;
  const uniqueIps = buildUniqueIpSets(loadedProps.unique_ips);

  return (
    <>
      <AdminDetailSearch dateStart={loadedProps.date_start} dateEnd={loadedProps.date_end} />

      <AdminDetailStats
        dateStart={loadedProps.date_start}
        dateEnd={loadedProps.date_end}
        dateStarts={loadedProps.date_starts}
        stats={loadedProps.stats}
      />

      <AdminDetailDemographics stats={loadedProps.stats} />

      <Container className="py-3">
        <AdminDetailInbounds inbounds={loadedProps.inbounds} uniqueIps={uniqueIps} />
        <AdminDetailDownloads downloads={loadedProps.downloads} uniqueIps={uniqueIps} />
        <AdminDetailPurchases
          purchases={loadedProps.purchases}
          uniqueIps={uniqueIps}
          activationCountsByKey={loadedProps.activation_counts_by_key}
        />
        <AdminDetailActivations
          activations={loadedProps.activations}
          uniqueIps={uniqueIps}
          activationCountsByKey={loadedProps.activation_counts_by_key}
        />
        <AdminDetailUpdateChecks updateChecks={loadedProps.update_checks} uniqueIps={uniqueIps} stats={loadedProps.stats} />
      </Container>
    </>
  );
}
